import { useState } from 'react';
import {
  Modal,
  Stack,
  Group,
  Button,
  Text,
  Checkbox,
  TextInput,
  Badge,
  Loader,
  Box,
} from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';
import { useAssets } from '@/hooks/api/useAssets';
import { useAddMultipleAssetsToAudit } from '@/hooks/api/useAudits';
import { notifications } from '@mantine/notifications';

interface AddAssetsModalProps {
  opened: boolean;
  onClose: () => void;
  auditEventId: string;
  organizationId: string;
}

export function AddAssetsModal({
  opened,
  onClose,
  auditEventId,
  organizationId,
}: AddAssetsModalProps) {
  const { data: assets = [], isLoading } = useAssets(organizationId);
  const addAssets = useAddMultipleAssetsToAudit();

  const [selectedAssetIds, setSelectedAssetIds] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');

  const handleToggleAsset = (assetId: string) => {
    setSelectedAssetIds((prev) =>
      prev.includes(assetId) ? prev.filter((id) => id !== assetId) : [...prev, assetId]
    );
  };

  const handleToggleAll = () => {
    if (selectedAssetIds.length === filteredAssets.length) {
      setSelectedAssetIds([]);
    } else {
      setSelectedAssetIds(filteredAssets.map((a) => a.id));
    }
  };

  const handleSubmit = async () => {
    if (selectedAssetIds.length === 0) {
      notifications.show({
        title: 'No Assets Selected',
        message: 'Please select at least one asset to add',
        color: 'orange',
      });
      return;
    }

    try {
      const result = await addAssets.mutateAsync({
        organization_id: organizationId,
        audit_event_id: auditEventId,
        asset_ids: selectedAssetIds,
      });

      notifications.show({
        title: 'Assets Added',
        message: `${result.added.length} assets added successfully${
          result.skipped > 0 ? ` (${result.skipped} already in audit)` : ''
        }`,
        color: 'green',
      });

      setSelectedAssetIds([]);
      setSearchTerm('');
      onClose();
    } catch (error: any) {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to add assets',
        color: 'red',
      });
    }
  };

  const handleClose = () => {
    setSelectedAssetIds([]);
    setSearchTerm('');
    onClose();
  };

  // Filter assets by search term
  const filteredAssets = assets.filter((asset) => {
    if (!searchTerm) return true;

    const search = searchTerm.toLowerCase();
    return (
      asset.tag_id?.toLowerCase().includes(search) ||
      asset.description?.toLowerCase().includes(search) ||
      asset.brand?.toLowerCase().includes(search) ||
      asset.model?.toLowerCase().includes(search) ||
      asset.serial_number?.toLowerCase().includes(search)
    );
  });

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Add Assets to Audit"
      size="lg"
      styles={{
        body: {
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          maxHeight: 'calc(100vh - 120px)',
        },
      }}
    >
      {/* Scrollable content area */}
      <Box
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '1rem',
        }}
      >
        <Stack gap="md">
          <TextInput
            placeholder="Search by tag, description, brand, model, or serial number..."
            leftSection={<IconSearch size={16} />}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />

          <Group justify="apart">
            <Text size="sm" c="dimmed">
              {selectedAssetIds.length} of {filteredAssets.length} selected
            </Text>
            <Button variant="subtle" size="xs" onClick={handleToggleAll}>
              {selectedAssetIds.length === filteredAssets.length
                ? 'Deselect All'
                : 'Select All'}
            </Button>
          </Group>

          <Stack gap="xs">
            {isLoading ? (
              <Group justify="center" py="xl">
                <Loader size="sm" />
                <Text size="sm" c="dimmed">
                  Loading assets...
                </Text>
              </Group>
            ) : filteredAssets.length === 0 ? (
              <Text size="sm" c="dimmed" ta="center" py="xl">
                {searchTerm ? 'No assets found matching your search' : 'No assets available'}
              </Text>
            ) : (
              filteredAssets.map((asset) => (
                <Group
                  key={asset.id}
                  gap="md"
                  p="sm"
                  style={{
                    borderRadius: 'var(--mantine-radius-sm)',
                    border: '1px solid var(--mantine-color-default-border)',
                    cursor: 'pointer',
                  }}
                  onClick={() => handleToggleAsset(asset.id)}
                >
                  <Checkbox
                    checked={selectedAssetIds.includes(asset.id)}
                    onChange={() => handleToggleAsset(asset.id)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <div style={{ flex: 1 }}>
                    <Group gap="xs" mb={4}>
                      <Text size="sm" fw={500}>
                        {asset.tag_id}
                      </Text>
                      <Badge size="sm" variant="light">
                        {asset.status}
                      </Badge>
                    </Group>
                    <Text size="xs" c="dimmed">
                      {asset.description}
                    </Text>
                    {asset.brand && asset.model && (
                      <Text size="xs" c="dimmed">
                        {asset.brand} - {asset.model}
                      </Text>
                    )}
                  </div>
                </Group>
              ))
            )}
          </Stack>
        </Stack>
      </Box>

      {/* Fixed bottom buttons */}
      <Box
        p="md"
        style={{
          borderTop: '1px solid var(--mantine-color-gray-3)',
          backgroundColor: 'var(--mantine-color-body)',
          flexShrink: 0,
        }}
      >
        <Group justify="flex-end">
          <Button variant="subtle" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            loading={addAssets.isPending}
            disabled={selectedAssetIds.length === 0}
          >
            Add {selectedAssetIds.length > 0 ? `(${selectedAssetIds.length})` : ''} Assets
          </Button>
        </Group>
      </Box>
    </Modal>
  );
}
